'use client';
import { OnbForm } from '@/components/form';
import { useStepData } from '@/components/stepData';
import { RootState } from '@/redux/store';
import { Box, Stack, Typography } from '@mui/material';
import { identityVerificationStart } from '@repo/onb-api';
import { COLORS, Ekyc, MaterialIcon, MaterialIconType, Metamap, StatusLottie } from '@repo/ui';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

type Tip = { icon: MaterialIconType; text: string };

// SHORT_BIOMETRY: selfie only
const SHORT_TIPS: Tip[] = [
  { icon: 'wb_sunny', text: 'Buscá un fondo claro y bien iluminado' },
  { icon: 'visibility', text: 'Poné el celular a la altura de tus ojos' },
  { icon: 'sentiment_very_satisfied', text: 'Evitá tener objetos que te tapen la cara (como lentes o tapabocas)' },
];

// FULL_BIOMETRY: DNI + selfie
const FULL_TIPS: Tip[] = [
  { icon: 'ar_on_you', text: 'Buscá un lugar con luz, con fondo claro y poné el celular a la altura de tus ojos.' },
  { icon: 'id_card', text: 'Evitá el uso del flash y girá el celular para lograr una imagen más nítida.' },
];

// Tip with a two-ring icon chip, as in customer-mobile's IdentityVerificationStart; greyed out while MetaMap submits
const TipRow = ({ icon, text, disabled }: Tip & { disabled: boolean }) => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 2.5 }}>
    <Box
      aria-hidden="true"
      sx={{
        flexShrink: 0,
        width: 56,
        height: 56,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: disabled ? COLORS.surface.muted : COLORS.surface.brandTint,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: disabled ? COLORS.border.subtle : COLORS.background[1],
        }}
      >
        <MaterialIcon
          icon={icon}
          size={24}
          color={disabled ? 'disabled' : 'primary'}
          sx={{ lineHeight: '1 !important' }}
        />
      </Box>
    </Box>
    <Typography
      variant="subtitle1"
      sx={{ lineHeight: '1.25rem', color: disabled ? COLORS.text.placeholder : undefined }}
    >
      {text}
    </Typography>
  </Stack>
);

export default function IdentityVerification({
  stepname: stepName,
  stepdata: stepDataProp,
}: {
  stepname: identityVerificationStart['name'];
  stepdata?: identityVerificationStart['variables'];
}): React.JSX.Element {
  const stepData = useStepData(stepDataProp);
  const [submitting, setSubmitting] = useState(false);
  const submitRef = useRef<HTMLButtonElement>(null);
  const instanceId = useSelector((state: RootState) => state.flowdata.id);

  useEffect(() => {
    setSubmitting(false);
  }, [stepData?.attempt]);

  return (
    <StepWrapper>
      {stepData?.flowType === 'SHORT_BIOMETRY' ? (
        <>
          {(stepData?.attempt || 0) > 0 ? (
            <>
              <Stack sx={{ alignItems: 'center', pb: 1 }}>
                <StatusLottie type="error" />
              </Stack>
              <Typography variant="h1">No pudimos validar tu identidad.</Typography>
              <Typography component="p" variant="subtitle2">
                Intentá nuevamente
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h1" color={submitting ? COLORS.text.placeholder : undefined}>
                ¡Genial! Ahora vamos a validar tu identidad
              </Typography>
              <Typography component="p" variant="subtitle2" color={submitting ? COLORS.text.placeholder : undefined}>
                Te vamos a pedir una selfie. Podés seguír estos tips:
              </Typography>
              <Stack
                sx={{
                  alignItems: 'center',
                  height: 134,
                  margin: '16px auto',
                  aspectRatio: '1/1',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                    borderRadius: '50%',
                    bgcolor: submitting ? COLORS.surface.muted : COLORS.background[1],
                  }}
                >
                  <MaterialIcon
                    icon="familiar_face_and_zone"
                    color={submitting ? 'disabled' : 'primary'}
                    size={88}
                    aria-hidden="true"
                    // MaterialIcon renders as a left-aligned table box: a centered 1em block keeps the glyph mid-circle
                    sx={{
                      display: 'block !important',
                      width: '1em',
                      height: '1em',
                      lineHeight: '1 !important',
                      textAlign: 'center',
                    }}
                  />
                </Box>
              </Stack>
            </>
          )}
          <Stack sx={{ gap: 3, pb: 2 }}>
            {SHORT_TIPS.map(({ icon, text }) => (
              <TipRow key={icon} icon={icon} text={text} disabled={submitting} />
            ))}
          </Stack>
        </>
      ) : (
        <>
          {(stepData?.attempt || 0) > 0 ? (
            <>
              <Stack sx={{ alignItems: 'center', pb: 1 }}>
                <StatusLottie type="error" />
              </Stack>
              <Typography variant="h1">No pudimos validar tu identidad.</Typography>
              <Typography component="p" variant="subtitle2">
                Intentá nuevamente
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h1" color={submitting ? COLORS.text.placeholder : undefined}>
                ¡Genial! Ahora vamos a validar tu identidad
              </Typography>
              <Typography component="p" variant="subtitle2" color={submitting ? COLORS.text.placeholder : undefined}>
                Buscá tu DNI y preparate para una selfie. Podés seguír estos tips:
              </Typography>
              <Stack sx={{ py: 1 }}>
                <Ekyc />
              </Stack>
            </>
          )}
          <Stack sx={{ gap: 3, pb: 2 }}>
            {FULL_TIPS.map(({ icon, text }) => (
              <TipRow key={icon} icon={icon} text={text} disabled={submitting} />
            ))}
          </Stack>
        </>
      )}
      <OnbForm<identityVerificationStart> stepName={stepName} hideSubmit sx={{ justifyContent: 'flex-end' }}>
        <Stack>
          <button ref={submitRef} style={{ display: 'none' }}></button>
          <Metamap
            submitLabel={(stepData?.attempt || 0) > 0 ? 'Reintentar' : 'Comenzar'}
            submitting={submitting}
            clientId={stepData?.clientId}
            flowId={stepData?.flowId}
            identityId={stepData?.identityId}
            traceId={stepData?.traceId}
            instanceId={instanceId}
            callback={() => {
              setSubmitting(true);
              submitRef.current?.click();
            }}
          />
        </Stack>
      </OnbForm>
    </StepWrapper>
  );
}
